/**
 * TopicPicker — shared, tree-aware topic selection component (iter 105).
 *
 * Design (per user + agent alignment):
 *   • Browse mode (dropdown open, no query): indent tree with ▸ chevrons
 *     for expand/collapse. Familiar, hierarchy-preserving.
 *   • Search mode (user types): flatten to searchable list. Each row shows
 *     the full breadcrumb path ("Rules › SOLAS › LSA"). Instant find at
 *     any depth.
 *   • Trigger label: once a value is picked, shows the full breadcrumb so
 *     the user always sees "which Life Rafts" (not just the leaf name).
 *   • Long labels wrap gracefully via break-words + line-clamp-2. No
 *     overflow past the container width regardless of depth.
 *
 * Usage:
 *   <TopicPicker
 *     topics={topics}                    // flat array; component builds the tree
 *     value={topicId}                    // selected topic id (or null)
 *     onChange={setTopicId}
 *     placeholder="Filter by topic"      // shown when nothing selected
 *     allowClear                          // adds a "× Clear" affordance
 *     testIdPrefix="oral-topic"          // used for data-testid on all interactive parts
 *   />
 */
import { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronDown, Search, X, Check, ArrowLeft, FolderTree } from 'lucide-react';
import { countDescendants, getSubtreeIds } from './topicPickerHelpers';

const _byParent = (topics) => {
  const map = new Map();
  topics.forEach((t) => {
    const parent = t.parent_topic_id || null;
    if (!map.has(parent)) map.set(parent, []);
    map.get(parent).push(t);
  });
  return map;
};

const _breadcrumb = (topics, topicId) => {
  const byId = new Map(topics.map((t) => [t.id, t]));
  const parts = [];
  let cur = byId.get(topicId);
  let hops = 0;
  while (cur && hops < 20) {
    parts.unshift(cur.name);
    cur = cur.parent_topic_id ? byId.get(cur.parent_topic_id) : null;
    hops += 1;
  }
  return parts;
};

const TreeNode = ({ node, byParent, depth, expanded, onToggle, onPick, selectedId, testIdPrefix }) => {
  const children = byParent.get(node.id) || [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(node.id);
  const isSelected = selectedId === node.id;

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => onPick(node.id)}
        className={`w-full flex items-start gap-1.5 text-left px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors ${
          isSelected ? 'bg-[#0A7EA4]/10 text-[#0A7EA4] font-medium' : 'text-slate-700'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        data-testid={`${testIdPrefix}-node-${node.id}`}
      >
        {hasChildren ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            className="mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-slate-200"
            data-testid={`${testIdPrefix}-toggle-${node.id}`}
          >
            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </span>
        ) : (
          <span className="mt-0.5 flex-shrink-0 w-4 h-4" />
        )}
        <span className="flex-1 min-w-0 break-words text-sm leading-snug line-clamp-2">
          {node.name}
        </span>
        {isSelected && <Check className="mt-0.5 flex-shrink-0 w-3.5 h-3.5" />}
      </button>
      {hasChildren && isOpen && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              byParent={byParent}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onPick={onPick}
              selectedId={selectedId}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const TopicPicker = ({
  topics = [],
  value = null,
  onChange,
  placeholder = 'Select topic…',
  allowClear = false,
  disabled = false,
  className = '',
  testIdPrefix = 'topic-picker',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  // Focused mode — same contract as TopicPickerMulti: clicking the
  // "explore inside" chevron on a search result re-roots the tree +
  // scopes the search to a subtree. Back button in the header pill
  // clears it.
  const [focusRootId, setFocusRootId] = useState(null);
  const rootRef = useRef(null);

  const byParent = useMemo(() => _byParent(topics), [topics]);
  const roots = byParent.get(null) || [];
  const effectiveRoots = useMemo(
    () => (focusRootId ? (byParent.get(focusRootId) || []) : roots),
    [focusRootId, byParent, roots]
  );
  const focusScopeIds = useMemo(
    () => (focusRootId ? getSubtreeIds(byParent, focusRootId) : null),
    [focusRootId, byParent]
  );
  const focusPath = useMemo(
    () => (focusRootId ? _breadcrumb(topics, focusRootId) : []),
    [focusRootId, topics]
  );

  const selectedBreadcrumb = useMemo(
    () => (value ? _breadcrumb(topics, value) : []),
    [value, topics]
  );

  // Auto-expand ancestors of the currently-selected node when opening,
  // so the user sees where their pick lives in the tree.
  useEffect(() => {
    if (!open || !value) return;
    const byId = new Map(topics.map((t) => [t.id, t]));
    const toOpen = new Set(expanded);
    let cur = byId.get(value);
    let hops = 0;
    while (cur && cur.parent_topic_id && hops < 20) {
      toOpen.add(cur.parent_topic_id);
      cur = byId.get(cur.parent_topic_id);
      hops += 1;
    }
    setExpanded(toOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleNode = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pickTopic = (id) => {
    onChange?.(id);
    setOpen(false);
    setQuery('');
  };

  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return topics
      .filter((t) => {
        if (focusScopeIds && !focusScopeIds.has(t.id)) return false;
        return t.name.toLowerCase().includes(q);
      })
      .map((t) => ({
        ...t,
        path: _breadcrumb(topics, t.id),
        childCount: countDescendants(byParent, t.id),
      }))
      .slice(0, 50);
  }, [query, topics, focusScopeIds, byParent]);

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={`${testIdPrefix}-root`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`w-full min-h-[38px] px-3 py-2 text-left border border-slate-300 rounded-md bg-white hover:border-[#0A7EA4] focus:outline-none focus:ring-2 focus:ring-[#0A7EA4]/30 transition-colors disabled:bg-slate-50 disabled:cursor-not-allowed`}
        data-testid={`${testIdPrefix}-trigger`}
      >
        {value && selectedBreadcrumb.length > 0 ? (
          <div className="flex items-start gap-1.5">
            <span className="flex-1 min-w-0 text-sm text-slate-700 break-words line-clamp-2">
              {selectedBreadcrumb.map((seg, idx) => (
                <span key={idx}>
                  {idx > 0 && <span className="mx-1 text-slate-400">›</span>}
                  <span className={idx === selectedBreadcrumb.length - 1 ? 'font-medium text-slate-900' : ''}>
                    {seg}
                  </span>
                </span>
              ))}
            </span>
            {allowClear && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(null);
                }}
                className="flex-shrink-0 p-0.5 rounded hover:bg-slate-200"
                data-testid={`${testIdPrefix}-clear`}
              >
                <X className="w-3.5 h-3.5 text-slate-500" />
              </span>
            )}
          </div>
        ) : (
          <span className="text-sm text-slate-400">{placeholder}</span>
        )}
      </button>

      {open && (
        <div
          className="absolute z-20 mt-1 w-full min-w-[280px] bg-white border border-slate-300 rounded-md shadow-lg max-h-[360px] flex flex-col"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {focusRootId && (
            <div
              className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs"
              data-testid={`${testIdPrefix}-focus-pill`}
            >
              <button
                type="button"
                onClick={() => { setFocusRootId(null); setExpanded(new Set()); }}
                className="flex-shrink-0 inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
                data-testid={`${testIdPrefix}-focus-back`}
                title="Return to full topic list"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 flex-shrink-0">Inside:</span>
              <span className="min-w-0 break-words leading-snug text-slate-800">
                {focusPath.map((seg, idx) => (
                  <span key={idx}>
                    {idx > 0 && <span className="mx-1 text-slate-400">›</span>}
                    <span className={idx === focusPath.length - 1 ? 'font-medium' : ''}>{seg}</span>
                  </span>
                ))}
              </span>
            </div>
          )}

          <div className="p-2 border-b border-slate-200 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={focusRootId ? 'Search inside subtree…' : 'Search topics…'}
              className="flex-1 min-w-0 text-sm outline-none placeholder:text-slate-400"
              data-testid={`${testIdPrefix}-search`}
              autoFocus
            />
          </div>

          <div className="flex-1 overflow-y-auto p-1">
            {searchResults ? (
              searchResults.length === 0 ? (
                <div className="p-3 text-sm text-slate-500 text-center">No topics match "{query}"</div>
              ) : (
                searchResults.map((t) => {
                  const hasChildren = t.childCount > 0;
                  return (
                    <div
                      key={t.id}
                      className={`flex items-start gap-1 px-2 py-1.5 rounded-md hover:bg-slate-100 ${
                        value === t.id ? 'bg-[#0A7EA4]/10' : ''
                      }`}
                      data-testid={`${testIdPrefix}-search-result-${t.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => pickTopic(t.id)}
                        className="flex-1 min-w-0 text-left cursor-pointer"
                      >
                        <span className="block text-sm text-slate-700 break-words leading-snug">
                          {t.path.map((seg, idx) => (
                            <span key={idx}>
                              {idx > 0 && <span className="mx-1 text-slate-400">›</span>}
                              <span className={idx === t.path.length - 1 ? 'font-medium text-slate-900' : ''}>
                                {seg}
                              </span>
                            </span>
                          ))}
                          {hasChildren && (
                            <span className="ml-1.5 text-[10px] text-slate-400 whitespace-nowrap">
                              · {t.childCount} inside
                            </span>
                          )}
                        </span>
                      </button>
                      {hasChildren && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFocusRootId(t.id);
                            setQuery('');
                            setExpanded(new Set());
                          }}
                          className="flex-shrink-0 mt-0.5 p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200"
                          data-testid={`${testIdPrefix}-drill-${t.id}`}
                          title={`Explore inside ${t.name}`}
                        >
                          <FolderTree className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })
              )
            ) : effectiveRoots.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 text-center">
                {focusRootId ? 'No subtopics inside this branch.' : 'No topics available'}
              </div>
            ) : (
              effectiveRoots.map((root) => (
                <TreeNode
                  key={root.id}
                  node={root}
                  byParent={byParent}
                  depth={0}
                  expanded={expanded}
                  onToggle={toggleNode}
                  onPick={pickTopic}
                  selectedId={value}
                  testIdPrefix={testIdPrefix}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicPicker;
